import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Divider,
  Grid,
  Typography,
  useTheme,
  Paper,
  TextField,
  Snackbar,
  Alert,
  IconButton
} from '@mui/material';
import {
  People as PeopleIcon,
  ContentCut as ContentCutIcon,
  Assignment as AssignmentIcon,
  Payments as PaymentsIcon,
  LocalShipping as LocalShippingIcon,
  Receipt as ReceiptIcon,
  Dashboard as DashboardIcon,
  Security as SecurityIcon,
  ArrowForward as ArrowForwardIcon,
  Email as EmailIcon,
  Send as SendIcon,
  ArrowBackIosNew,
  ArrowForwardIos
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import PublicNavbar from '../components/layout/PublicNavbar';

const LandingPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Contact form state
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  // Carousel state
  const [carouselIndex, setCarouselIndex] = useState(0);

  // Handle scrolling to sections based on state from navigation
  useEffect(() => {
    if (location.state) {
      setTimeout(() => {
        if ((location.state as any).scrollToFeatures) {
          const featuresSection = document.getElementById('features-section');
          if (featuresSection) {
            featuresSection.scrollIntoView({ behavior: 'smooth' });
          }
        } else if ((location.state as any).scrollToPricing) {
          const pricingSection = document.getElementById('pricing-section');
          if (pricingSection) {
            pricingSection.scrollIntoView({ behavior: 'smooth' });
          }
        } else if ((location.state as any).scrollToContact) {
          const contactSection = document.getElementById('contact-section');
          if (contactSection) {
            contactSection.scrollIntoView({ behavior: 'smooth' });
          }
        }
      }, 100); // Small delay to ensure DOM is ready
    }
  }, [location.state]);

  const handleLogin = () => {
    navigate('/login');
  };

  const handleContactSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // In a real implementation, you would send this data to a backend API
    // But for simplicity, we'll just open the default mail client
    const mailtoLink = `mailto:contactus@stitchflo.app?subject=Contact request from ${name}&body=${message}%0D%0A%0D%0AFrom: ${name}%0D%0AEmail: ${email}`;
    window.location.href = mailtoLink;
    
    // Reset form
    setName('');
    setEmail('');
    setMessage('');
    setSnackbarOpen(true);
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const primaryColor = theme.palette.primary.main;

  const modules = [
    {
      title: 'Dashboard & Analytics',
      description: 'Real-time overview of your entire factory — orders, production, inventory, and financials at a glance',
      icon: <DashboardIcon fontSize="large" sx={{ color: primaryColor }} />
    },
    {
      title: 'Purchase & Inventory',
      description: 'Manage purchase orders, material inwards, inventory tracking, allocations, and semi-finished goods',
      icon: <ReceiptIcon fontSize="large" sx={{ color: primaryColor }} />
    },
    {
      title: 'Designs & Costing',
      description: 'Create designs with images, BOM, fabric consumption, and auto-calculated cost prices',
      icon: <ContentCutIcon fontSize="large" sx={{ color: primaryColor }} />
    },
    {
      title: 'Job Works',
      description: 'Issue materials to internal & external vendors, track receipts, invoicing, and pending deliveries',
      icon: <AssignmentIcon fontSize="large" sx={{ color: primaryColor }} />
    },
    {
      title: 'Production Tracking',
      description: 'End-to-end production flow — cutting, stitching, pressing, QC, repairs, and barcoding stages',
      icon: <LocalShippingIcon fontSize="large" sx={{ color: primaryColor }} />
    },
    {
      title: 'Workers & Payments',
      description: 'Manage workers, assign tasks with piece-rate tracking, and automate payment calculations',
      icon: <PeopleIcon fontSize="large" sx={{ color: primaryColor }} />
    },
    {
      title: 'Quality Control',
      description: 'Dedicated QC and press stations with defect tracking, repair queues, and rework management',
      icon: <SecurityIcon fontSize="large" sx={{ color: primaryColor }} />
    },
    {
      title: 'Expense Management',
      description: 'Record and categorize all business expenses with vendor-wise and category-wise reporting',
      icon: <PaymentsIcon fontSize="large" sx={{ color: primaryColor }} />
    },
    {
      title: 'User & Role Management',
      description: 'Granular role-based permissions — control exactly who can view, add, edit, or delete in each module',
      icon: <SecurityIcon fontSize="large" sx={{ color: primaryColor }} />
    }
  ];

  const bannerImages = [
    '/banner1.png',
    '/banner2.png',
    '/banner3.png',
    '/banner4.png',
  ];

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
      {/* Navigation Bar */}
      <PublicNavbar />
      
      {/* Hero Section */}
      <Box
        sx={{
          bgcolor: 'background.paper',
          pt: { xs: 8, sm: 12 },
          pb: { xs: 8, sm: 12 },
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(25, 118, 210, 0.05)',
            zIndex: 0
          }}
        />
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography
                variant="h2"
                component="h1"
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: '2.5rem', md: '3.5rem' },
                  mb: 2,
                  color: 'text.primary'
                }}
              >
                StitchFlo
              </Typography>
              <Typography
                variant="h5"
                sx={{
                  mb: 4,
                  color: 'text.secondary',
                  fontWeight: 400
                }}
              >
                Streamline your garment factory operations with our comprehensive management solution
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                <Button
                  variant="contained"
                  size="large"
                  onClick={handleLogin}
                  endIcon={<ArrowForwardIcon />}
                  sx={{
                    px: 4,
                    py: 1.5,
                    borderRadius: 2,
                    textTransform: 'none',
                    fontSize: '1.1rem',
                    fontWeight: 600,
                  }}
                >
                  Sign In to Dashboard
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => {
                    const contactSection = document.getElementById('contact-section');
                    if (contactSection) {
                      contactSection.scrollIntoView({ behavior: 'smooth' });
                    }
                  }}
                  endIcon={<EmailIcon />}
                  sx={{
                    px: 4,
                    py: 1.5,
                    borderRadius: 2,
                    textTransform: 'none',
                    fontSize: '1.1rem',
                    fontWeight: 600,
                  }}
                >
                  Get in Touch
                </Button>
              </Box>
            </Grid>
            <Grid item xs={12} md={6} sx={{ display: { xs: 'none', md: 'block' } }}>
              {/* Carousel Gallery */}
              <Box sx={{ position: 'relative', width: '100%', maxWidth: 500, margin: '0 auto' }}>
                <Box sx={{ position: 'relative', overflow: 'hidden', borderRadius: 2 }}>
              <Box
                component="img"
                    src={bannerImages[carouselIndex]}
                    alt={`App screenshot ${carouselIndex + 1}`}
                    sx={{ width: '100%', height: 320, objectFit: 'cover', transition: 'all 0.4s' }}
                  />
                  {/* Left Arrow */}
                  <IconButton
                    onClick={() => setCarouselIndex((prev) => (prev === 0 ? bannerImages.length - 1 : prev - 1))}
                    sx={{ position: 'absolute', top: '50%', left: 8, transform: 'translateY(-50%)', bgcolor: 'background.paper', boxShadow: 1, zIndex: 2 }}
                  >
                    <ArrowBackIosNew />
                  </IconButton>
                  {/* Right Arrow */}
                  <IconButton
                    onClick={() => setCarouselIndex((prev) => (prev === bannerImages.length - 1 ? 0 : prev + 1))}
                    sx={{ position: 'absolute', top: '50%', right: 8, transform: 'translateY(-50%)', bgcolor: 'background.paper', boxShadow: 1, zIndex: 2 }}
                  >
                    <ArrowForwardIos />
                  </IconButton>
                </Box>
                {/* Dots */}
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                  {bannerImages.map((_, idx) => (
                    <Box
                      key={idx}
                sx={{ 
                        width: 12,
                        height: 12,
                        borderRadius: '50%',
                        bgcolor: idx === carouselIndex ? 'primary.main' : 'grey.400',
                        mx: 0.5,
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                      }}
                      onClick={() => setCarouselIndex(idx)}
                    />
                  ))}
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Features Section */}
      <Box id="features-section">
        <Container maxWidth="lg" sx={{ py: { xs: 6, sm: 8 } }}>
          <Typography
            variant="h3"
            component="h2"
            align="center"
            sx={{ mb: 6, fontWeight: 600 }}
          >
            Complete Factory Management Solution
          </Typography>

          <Grid container spacing={3}>
            {modules.map((module, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Card
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    '&:hover': {
                      transform: 'translateY(-5px)',
                      boxShadow: 4
                    }
                  }}
                >
                  <CardContent sx={{ flexGrow: 1, p: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                      {module.icon}
                    </Box>
                    <Typography
                      variant="h5"
                      component="h3"
                      align="center"
                      gutterBottom
                      sx={{ fontWeight: 600 }}
                    >
                      {module.title}
                    </Typography>
                    <Typography
                      variant="body1"
                      align="center"
                      color="text.secondary"
                    >
                      {module.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>
      
      {/* Pricing Section */}
      <Box id="pricing-section" sx={{ bgcolor: 'background.paper', py: { xs: 6, sm: 8 } }}>
        <Container maxWidth="lg">
          <Typography
            variant="h3"
            component="h2"
            align="center"
            sx={{ mb: 2, fontWeight: 600 }}
          >
            Simple, Transparent Pricing
          </Typography>
          
          <Typography
            variant="h6"
            align="center"
            color="text.secondary"
            sx={{ mb: 6, maxWidth: 700, mx: 'auto' }}
          >
            Pay only for what you use with our straightforward per-worker pricing model.
          </Typography>
          
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Paper 
              elevation={4}
              sx={{
                p: 4,
                maxWidth: 500,
                borderRadius: 3,
                position: 'relative',
                overflow: 'hidden'
              }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  right: 0,
                  width: 100,
                  height: 100,
                  background: `linear-gradient(135deg, transparent 50%, ${primaryColor} 50%)`,
                  zIndex: 0
                }}
              />
              <Box sx={{ position: 'relative', zIndex: 1 }}>
                <Typography variant="h4" component="h3" gutterBottom fontWeight={700}>
                  ₹50
                </Typography>
                <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                  per active worker / month
                </Typography>
                
                <Divider sx={{ my: 2 }} />
                
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body1" sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
                    <ArrowForwardIcon sx={{ mr: 1, color: primaryColor, fontSize: 18 }} />
                    Access to all modules and features
                  </Typography>
                  
                  <Typography variant="body1" sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
                    <ArrowForwardIcon sx={{ mr: 1, color: primaryColor, fontSize: 18 }} />
                    Unlimited admin users at no extra cost
                  </Typography>
                  
                  <Typography variant="body1" sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
                    <ArrowForwardIcon sx={{ mr: 1, color: primaryColor, fontSize: 18 }} />
                    Secure cloud-based storage
                  </Typography>
                  
                  <Typography variant="body1" sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
                    <ArrowForwardIcon sx={{ mr: 1, color: primaryColor, fontSize: 18 }} />
                    Pay only for active workers in your system
                  </Typography>
                  
                  <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center' }}>
                    <ArrowForwardIcon sx={{ mr: 1, color: primaryColor, fontSize: 18 }} />
                    Real-time dashboards and reports
                  </Typography>
                </Box>
                
                <Button
                  variant="contained"
                  fullWidth
                  size="large"
                  onClick={handleLogin}
                  sx={{
                    mt: 2,
                    py: 1.5,
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                  }}
                >
                  Get Started Now
                </Button>
              </Box>
            </Paper>
          </Box>
        </Container>
      </Box>
      
      {/* Contact Us Section */}
      <Box id="contact-section" sx={{ py: { xs: 6, sm: 8 } }}>
        <Container maxWidth="md">
          <Typography
            variant="h3"
            component="h2"
            align="center"
            sx={{ mb: 2, fontWeight: 600 }}
          >
            Contact Us
          </Typography>
          
          <Typography
            variant="h6"
            align="center"
            color="text.secondary"
            sx={{ mb: 6, maxWidth: 700, mx: 'auto' }}
          >
            Have questions about StitchFlo? We're here to help you streamline your factory operations.
          </Typography>
          
          <Paper
            elevation={3}
            sx={{
              p: { xs: 3, md: 5 },
              borderRadius: 2,
              maxWidth: 800,
              mx: 'auto'
            }}
          >
            <Grid container spacing={4}>
              <Grid item xs={12} md={5}>
                <Box sx={{ mb: 4 }}>
                  <EmailIcon fontSize="large" color="primary" sx={{ mb: 2 }} />
                  <Typography variant="h5" gutterBottom>
                    Get in Touch
                  </Typography>
                  <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                    Send us a message and we'll get back to you as soon as possible.
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    Email:
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    contactus@stitchflo.app
                  </Typography>
                </Box>
              </Grid>
              
              <Grid item xs={12} md={7}>
                <Box component="form" onSubmit={handleContactSubmit}>
                  <TextField
                    fullWidth
                    label="Your Name"
                    variant="outlined"
                    margin="normal"
                    required
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                  />
                  
                  <TextField
                    fullWidth
                    label="Email Address"
                    variant="outlined"
                    margin="normal"
                    required
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                  
                  <TextField
                    fullWidth
                    label="Message"
                    variant="outlined"
                    margin="normal"
                    required
                    multiline
                    rows={4}
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                  />
                  
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    size="large"
                    endIcon={<SendIcon />}
                    sx={{ mt: 2, py: 1.5, px: 4 }}
                  >
                    Send Message
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Container>
        
        {/* Success Snackbar */}
        <Snackbar 
          open={snackbarOpen} 
          autoHideDuration={6000} 
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert 
            onClose={handleSnackbarClose} 
            severity="success" 
            variant="filled"
            sx={{ width: '100%' }}
          >
            Your mail client has been opened with your message. Thanks for contacting us!
          </Alert>
        </Snackbar>
      </Box>
      
      {/* Footer */}
      <Box sx={{ bgcolor: 'background.default', py: 4, borderTop: '1px solid', borderColor: 'divider' }}>
        <Container>
          <Typography variant="body2" color="text.secondary" align="center">
            © {new Date().getFullYear()} StitchFlo. All rights reserved.
          </Typography>
        </Container>
      </Box>
    </Box>
  );
};

export default LandingPage; 